import { Entity } from '@backstage/catalog-model';

export type RuleSeverity = 'critical' | 'major' | 'minor';

export type Rule = {
  name: string;
  check: string;
  weight?: number;
  severity?: RuleSeverity;
  guidance?: string;
  learnMoreUrl?: string;
  impacts?: string[];
};

export type NormalizedRule = Rule & {
  weight: number;
  severity: RuleSeverity;
};

export type RuleCategory = {
  description?: string;
  checks: Rule[];
};

export type RuleCategoryMap = Record<string, RuleCategory>;

export type CategoryRuleEvaluation = {
  category: string;
  description?: string;
  completedChecks: NormalizedRule[];
  missingChecks: NormalizedRule[];
};

type CategoryResult = {
  category: string;
  beginner: number;
  bronze: number;
  silver: number;
  gold: number;
};

function runChecks(entity: Entity, checks: Rule[]) {
  let passed = 0;
  let passedWeight = 0;
  let totalWeight = 0;
  const failedChecks: NormalizedRule[] = [];
  const completedChecks: NormalizedRule[] = [];

  for (const rule of checks ?? []) {
    let ok = false;
    try {
      // eslint-disable-next-line no-new-func
      const fn = new Function('entity', `return !!(${rule.check});`);
      ok = !!fn(entity);
    } catch {
      ok = false;
    }

    const weight = rule.weight ?? 1;
    const severity = rule.severity ?? 'minor';
    const normalizedRule: NormalizedRule = { ...rule, weight, severity };

    totalWeight += weight;

    if (ok) {
      passed += 1;
      passedWeight += weight;
      completedChecks.push(normalizedRule);
    } else {
      failedChecks.push(normalizedRule);
    }
  }

  return {
    passed,
    total: checks?.length ?? 0,
    passedWeight,
    totalWeight,
    completedChecks,
    failedChecks,
  };
}

// Evaluate a single category for an entity (used for individual category checks)
function evaluateSingleCategory(entity: Entity, checks: Rule[]) {
  const { passedWeight, totalWeight } = runChecks(entity, checks);

  if (totalWeight === 0) return 'beginner';
  const percent = Math.round((passedWeight / totalWeight) * 100);

  if (percent === 100) return 'gold';
  if (percent >= 50) return 'silver';
  if (percent >= 1) return 'bronze';
  return 'beginner';
}

// Evaluate entity across ALL categories (returns overall level)
export function evaluateEntityCategory(entity: Entity, rules: RuleCategoryMap) {
  let total = 0;
  let passed = 0;
  let totalWeight = 0;
  let passedWeight = 0;

  for (const { checks } of Object.values(rules)) {
    const result = runChecks(entity, checks);
    total += result.total;
    passed += result.passed;
    totalWeight += result.totalWeight;
    passedWeight += result.passedWeight;
  }

  if (totalWeight === 0) return 'beginner';
  const percent = Math.round((passedWeight / totalWeight) * 100);

  if (percent === 100) return 'gold';
  if (percent >= 50) return 'silver';
  if (percent >= 1) return 'bronze';
  return 'beginner';
}

// Aggregate all entities into category-level maturity
export function aggregateMaturity(
  entities: Entity[],
  rules: RuleCategoryMap,
): CategoryResult[] {
  const results: Record<string, CategoryResult> = {};

  // Init counters
  for (const category of Object.keys(rules)) {
    results[category] = { category, beginner: 0, bronze: 0, silver: 0, gold: 0 };
  }

  for (const entity of entities) {
    for (const [category, { checks }] of Object.entries(rules)) {
      const tier = evaluateSingleCategory(entity, checks); // Use the single category function
      results[category][tier] += 1;
    }
  }

  return Object.values(results);
}

// ✅ Evaluate a single entity across ALL categories (for component cards)
export function evaluateEntity(
  entity: Entity,
  rules: RuleCategoryMap,
): { passed: number; total: number; passedWeight: number; totalWeight: number } {
  let passed = 0;
  let total = 0;
  let passedWeight = 0;
  let totalWeight = 0;

  for (const { checks } of Object.values(rules)) {
    const result = runChecks(entity, checks);
    total += result.total;
    passed += result.passed;
    passedWeight += result.passedWeight;
    totalWeight += result.totalWeight;
  }

  return { passed, total, passedWeight, totalWeight };
}

export function evaluateCampaign(
  entity: Entity,
  checks: Rule[],
): {
  passed: number;
  total: number;
  passedWeight: number;
  totalWeight: number;
  completedChecks: NormalizedRule[];
  missingChecks: NormalizedRule[];
} {
  const result = runChecks(entity, checks);
  return {
    passed: result.passed,
    total: result.total,
    passedWeight: result.passedWeight,
    totalWeight: result.totalWeight,
    completedChecks: result.completedChecks,
    missingChecks: result.failedChecks,
  };
}

export function evaluateEntityRuleCategories(
  entity: Entity,
  rules: RuleCategoryMap,
): CategoryRuleEvaluation[] {
  const outcomes: CategoryRuleEvaluation[] = [];

  for (const [category, { description, checks }] of Object.entries(rules)) {
    const result = runChecks(entity, checks);
    outcomes.push({
      category,
      description,
      completedChecks: result.completedChecks,
      missingChecks: result.failedChecks,
    });
  }

  return outcomes;
}
